import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Coins, KeyRound, Loader2, CheckCircle2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { StepShell, Field, inputCls } from "./WizardUI";

const CHOICES = [
  { v: "credits", l: "Use BroadcastFlow AI credits", d: "Simplest — everything runs on your plan's monthly credits." },
  { v: "byok", l: "Connect my own API keys", d: "Bring your own OpenAI, ElevenLabs, Gemini keys — pay providers directly." },
  { v: "both", l: "Use both", d: "Your keys where connected, platform credits everywhere else." },
];

export default function Step8Credits({ saved, onSave, onBack, onSkip, busy }) {
  const [choice, setChoice] = useState(saved?.usage_preference || "credits");
  const [balance, setBalance] = useState(null);
  const [trial, setTrial] = useState(null);
  const [providers, setProviders] = useState([]);
  const [keyInputs, setKeyInputs] = useState({});
  const [connecting, setConnecting] = useState("");

  useEffect(() => {
    api.get("/credits/balance").then((r) => setBalance(r.data)).catch(() => {});
    api.get("/trial/status").then((r) => setTrial(r.data)).catch(() => {});
    api.get("/providers").then((r) => {
      const list = (Array.isArray(r.data) ? r.data : r.data.providers || [])
        .filter((p) => p.mode === "byok");
      setProviders(list.slice(0, 8));
    }).catch(() => {});
  }, []);

  const connect = async (key) => {
    const api_key = (keyInputs[key] || "").trim();
    if (!api_key) { toast.error("Paste the API key first"); return; }
    setConnecting(key);
    try {
      await api.post(`/providers/${key}/connect`, { api_key });
      toast.success("Key saved (encrypted)");
      setKeyInputs((x) => ({ ...x, [key]: "" }));
      setProviders((arr) => arr.map((p) => (p.key === key ? { ...p, connected: true } : p)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save key"); }
    finally { setConnecting(""); }
  };

  return (
    <StepShell step={8} title="Credits or your own API keys?"
      subtitle="Choose how AI generations are powered. You can change this anytime."
      onBack={onBack} onContinue={() => onSave({ usage_preference: choice })} onSkip={onSkip} busy={busy}>
      <div className="grid sm:grid-cols-3 gap-2.5">
        {CHOICES.map((c) => (
          <button key={c.v} type="button" onClick={() => setChoice(c.v)} data-testid={`onb-usage-${c.v}`}
            className={`rounded-xl border p-4 text-left transition ${
              choice === c.v ? "border-brand-amber/70 bg-brand-amber/10" : "border-line bg-bg-surface hover:border-ink-muted"}`}>
            <div className={`text-sm font-semibold mb-1 ${choice === c.v ? "text-brand-amber" : "text-ink-primary"}`}>{c.l}</div>
            <div className="text-[11px] text-ink-muted">{c.d}</div>
          </button>
        ))}
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="onb-credit-balance">
          <div className="font-mono text-[9px] uppercase tracking-wider text-ink-muted mb-1">Current balance</div>
          <div className="text-xl font-bold text-ink-primary flex items-center gap-1.5">
            <Coins className="h-4 w-4 text-brand-amber" />
            {balance ? (balance.total_remaining == null ? "Unlimited" : balance.total_remaining) : "—"}
          </div>
        </div>
        <div className="rounded-xl border border-line bg-bg-surface p-4">
          <div className="font-mono text-[9px] uppercase tracking-wider text-ink-muted mb-1">Trial credits</div>
          <div className="text-xl font-bold text-ink-primary">
            {trial?.on_trial ? `${trial.credits_remaining ?? 0} (${trial.days_remaining ?? 0}d left)` : trial ? "No active trial" : "—"}
          </div>
        </div>
        <div className="rounded-xl border border-line bg-bg-surface p-4">
          <div className="font-mono text-[9px] uppercase tracking-wider text-ink-muted mb-1">Typical costs</div>
          <div className="text-[11px] text-ink-secondary leading-relaxed">Social post ≈ 1 · Bio ≈ 2 · Voice ≈ 3-5 credits</div>
        </div>
      </div>
      {(choice === "byok" || choice === "both") && (
        <div className="rounded-xl border border-line bg-bg-surface p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Connect provider keys</div>
            <a href="/dashboard/providers" target="_blank" rel="noreferrer"
              className="text-[11px] text-cyan-300 hover:text-cyan-100 inline-flex items-center gap-1">
              Full provider center <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          {providers.length === 0 && <div className="text-xs text-ink-muted">Loading providers…</div>}
          {providers.map((p) => (
            <div key={p.key} className="flex items-center gap-2" data-testid={`onb-byok-${p.key}`}>
              <div className="w-32 shrink-0 text-xs text-ink-primary truncate">{p.label || p.key}</div>
              {p.connected ? (
                <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300"><CheckCircle2 className="h-3.5 w-3.5" /> Key on file</span>
              ) : (
                <>
                  <input type="password" autoComplete="new-password" className={`${inputCls} !py-1.5 text-xs`}
                    placeholder="Paste API key…" value={keyInputs[p.key] || ""}
                    onChange={(e) => setKeyInputs((x) => ({ ...x, [p.key]: e.target.value }))} />
                  <button type="button" onClick={() => connect(p.key)} disabled={connecting === p.key}
                    className="shrink-0 inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-[11px] text-ink-secondary hover:text-ink-primary transition disabled:opacity-50">
                    {connecting === p.key ? <Loader2 className="h-3 w-3 animate-spin" /> : <KeyRound className="h-3 w-3" />} Save
                  </button>
                </>
              )}
            </div>
          ))}
          <p className="text-[10px] text-ink-muted">Keys are encrypted at rest and never displayed again.</p>
        </div>
      )}
      <div className="rounded-md border border-cyan-500/30 bg-cyan-500/5 px-3 py-2.5 text-[11px] text-cyan-200" data-testid="onb-credit-notice">
        Failed generations must not deduct credits — you're only charged when content is successfully created.
      </div>
    </StepShell>
  );
}
